import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Search,
  Plus,
  Tags,
  Edit3,
  Trash2,
  X,
  Save,
  AlertTriangle,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';

const categoryIcons = ['💊', '💉', '🩹', '🧴', '🩺', '🫁', '🩻', '🧬'];

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [editCat, setEditCat] = useState(null);
  const [deleteCat, setDeleteCat] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', description: '' });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const data = await api.get('/categories');
      setCategories(Array.isArray(data) ? data : (data?.categories || []));
    } catch (err) {
      toast.error(err.message || 'Failed to load categories');
    }
  };

  const filtered = categories.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.description && c.description.toLowerCase().includes(search.toLowerCase()))
  );

  const openAdd = () => {
    setAddForm({ name: '', description: '' });
    setShowAdd(true);
  };

  const saveNew = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim()) return;
    try {
      await api.post('/categories', { name: addForm.name, description: addForm.description });
      toast.success('Category added');
      setShowAdd(false);
      loadCategories();
    } catch (err) {
      toast.error(err.message || 'Failed to add category');
    }
  };

  const openEdit = (cat) => {
    setEditCat(cat);
    setEditForm({ ...cat });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/categories/${editForm.id}`, {
        name: editForm.name,
        description: editForm.description,
      });
      toast.success('Category updated');
      setEditCat(null);
      loadCategories();
    } catch (err) {
      toast.error(err.message || 'Failed to update category');
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/categories/${deleteCat.id}`);
      toast.success('Category deleted');
      setDeleteCat(null);
      loadCategories();
    } catch (err) {
      toast.error(err.message || 'Failed to delete category');
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Bar */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search categories..."
            className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-24 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          />
          <button className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md bg-gray-100 px-4 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-200">
            Find
          </button>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-600 active:scale-[0.97]"
        >
          <Plus className="h-4 w-4" />
          New Category
        </button>
      </div>

      {/* Grid */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-2">
            <Tags className="h-5 w-5 text-emerald-500" />
            <h2 className="text-base font-semibold text-gray-900">Categories</h2>
          </div>
          <span className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
            {categories.length} Categories
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-6">
          {filtered.map((cat, idx) => (
            <div key={cat.id} className="rounded-xl border border-gray-200 bg-gray-50 p-4 transition-all hover:shadow-md hover:border-emerald-200">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{categoryIcons[idx % categoryIcons.length]}</span>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">{cat.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{cat.description || 'No description'}</p>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 border-t border-gray-200 pt-3">
                <button onClick={() => openEdit(cat)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-amber-50 hover:text-amber-600"><Edit3 className="h-4 w-4" /></button>
                <button onClick={() => setDeleteCat(cat)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Tags className="mb-3 h-12 w-12 text-gray-300" />
            <p className="text-sm">No categories found</p>
          </div>
        )}
      </div>

      {/* ADD MODAL */}
      {showAdd &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAdd(false)} />
            <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Add New Category</h3>
                <button onClick={() => setShowAdd(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={saveNew} className="px-6 py-5">
                <div className="space-y-4">
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Category Name *</label>
                    <input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" required />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Description</label>
                    <textarea value={addForm.description} onChange={(e) => setAddForm({ ...addForm, description: e.target.value })} rows={3} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none resize-none" />
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-3 border-t border-gray-100 pt-5">
                  <button type="button" onClick={() => setShowAdd(false)} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"><Save className="h-4 w-4" />Save Category</button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* EDIT MODAL */}
      {editCat &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setEditCat(null)} />
            <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Edit Category</h3>
                <button onClick={() => setEditCat(null)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={saveEdit} className="px-6 py-5">
                <div className="space-y-4">
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Category Name</label>
                    <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Description</label>
                    <textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} rows={3} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none resize-none" />
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-3 border-t border-gray-100 pt-5">
                  <button type="button" onClick={() => setEditCat(null)} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"><Save className="h-4 w-4" />Save Changes</button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* DELETE CONFIRM MODAL */}
      {deleteCat &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDeleteCat(null)} />
            <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Category?</h3>
              <p className="mt-2 text-sm text-gray-500">Are you sure you want to delete <strong>{deleteCat.name}</strong>?</p>
              <div className="mt-6 flex justify-center gap-3">
                <button onClick={() => setDeleteCat(null)} className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                <button onClick={confirmDelete} className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700">Delete</button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
